import { useQuery } from '@tanstack/react-query';
import { useSearchResults } from './useSearchResults';
import { useUserStore } from '../../stores/userStore';
import { useWebLocale } from '../../contexts/WebLocaleContext';
import { searchApi } from '../../api/search';

export const searchItemKey = (item) =>
  `${item.albumSource || item.artistSource || ''}:${item.sourceId || item.id || item.artistId || item.albumId || item.slug}`;
const unique = (items) => {
  const seen = new Set();
  return items.filter((item) => {
    const key = searchItemKey(item);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

// Same summary seeding and playlist identities as SearchCategoryScreen in the
// app. The web keeps numbered pages and wider grids, backed by real API pages.
export function useSearchCategoryPage(query, category, page) {
  const term = (query || '').trim();
  const apiCategory = category === 'mixes' ? 'lists' : category;
  const size = ['reviews', 'artists'].includes(apiCategory) ? 20 : 10;
  const userId = useUserStore((state) => state.user?.id);
  const { locale } = useWebLocale();
  const summary = useSearchResults(term);
  const bucket =
    summary.data?.[{ reviews: 'albums', lists: 'playlists' }[apiCategory] || apiCategory];
  const preview = bucket?.items || [];
  const context =
    apiCategory === 'lists'
      ? {
          artistSourceId:
            summary.data?.artists?.items
              .map((item) => String(item.id ?? item.artistId ?? ''))
              .filter(Boolean) || [],
          albumSourceId:
            summary.data?.albums?.items.map((item) => item.sourceId).filter(Boolean) || [],
        }
      : {};
  const canReuse =
    page === 1 && preview.length > 0 && preview.length >= Math.min(size, bucket?.total ?? 0);
  const result = useQuery({
    queryKey: [
      'web-search-category-page',
      userId || 'guest',
      locale,
      apiCategory,
      term,
      page,
      context,
      summary.dataUpdatedAt,
    ],
    enabled: Boolean(term && !summary.isPending),
    staleTime: 30_000,
    retry: false,
    queryFn: async ({ signal }) => {
      if (canReuse) return preview.slice(0, size);
      const params = new URLSearchParams({ locale });
      // Notices use the existing unpaged endpoint; other categories use the app's page contract.
      if (apiCategory !== 'notices') {
        params.set('page', String(page));
        params.set('size', String(size));
      }
      Object.entries(context).forEach(([key, values]) =>
        values.forEach((value) => params.append(key, value)),
      );
      const response = await searchApi.searchCategory(apiCategory, term, {
        signal,
        authenticated: Boolean(userId),
        params,
      });
      return apiCategory === 'notices'
        ? response.data.slice((page - 1) * size, page * size)
        : response.data;
    },
  });
  const raw = result.data || [];
  const items = apiCategory === 'artists' && page === 1 ? unique([...preview, ...raw]) : raw;
  const knownTotal = Number.isFinite(bucket?.total) ? bucket.total : null;
  const hasNext = raw.length === size && (knownTotal == null || page * size < knownTotal);
  const totalPages = Math.max(
    page,
    knownTotal != null ? Math.ceil(knownTotal / size) : page + (hasNext ? 1 : 0),
  );
  return { ...result, data: result.data ? items : undefined, size, totalPages };
}
